import { Flex, Text, Box, Collapse, Link } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { useRef, useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { removeLanguage } from '../../../store/redux/slices/template.slice';
import { clearError } from '../../../store/redux/slices/templateErrors.slice';
import LocalisedField from './LocalisedField';
import RemoveLanguageModal from './modals/RemoveLanguageModal';
import { getLocaleDisplayName } from '../../../constants/locales';
import { CustomAlertTriangleIcon } from '../../../theme/icons/CustomAlertTriangleIcon';

const LanguagesWrapper = ({
  language,
  defaultIsOpen = false,
  completionText,
  compact = false,
  isOpen: controlledIsOpen,
  onToggle,
  useNewDesign = false,
}: {
  language: string;
  defaultIsOpen?: boolean;
  completionText?: string;
  compact?: boolean;
  isOpen?: boolean;
  onToggle?: (language: string) => void;
  useNewDesign?: boolean;
}) => {
  const headerFields = useAppSelector((s) => s.template.fields.header);
  const centralFields = useAppSelector((s) => s.template.fields.central);
  const detailsFields = useAppSelector((s) => s.template.fields.details);
  const backFields = useAppSelector((s) => s.template.fields.back);
  const dispatch = useAppDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [internalIsOpen, setInternalIsOpen] = useState(defaultIsOpen);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  useEffect(() => {
    if (!isControlled && defaultIsOpen) {
      setInternalIsOpen(true);
    }
  }, [defaultIsOpen, isControlled]);

  const handleRemoveLanguage = () => {
    const sectionFieldMap: Record<string, Record<string, unknown>> = {
      header: headerFields,
      central: centralFields,
      details: detailsFields,
      back: backFields,
    };
    Object.entries(sectionFieldMap).forEach(([section, sectionFields]) => {
      Object.keys(sectionFields || {}).forEach((slot) => {
        dispatch(
          clearError(`fields.${section}.${slot}.localisation.${language}`)
        );
      });
    });
    dispatch(removeLanguage(language));
  };

  const handleToggle = () => {
    const willOpen = isControlled ? !controlledIsOpen : !internalIsOpen;
    if (isControlled) {
      onToggle?.(language);
    } else {
      setInternalIsOpen(!internalIsOpen);
    }
    if (willOpen && useNewDesign) {
      setTimeout(() => {
        const el = wrapperRef.current;
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 20;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 350);
    }
  };

  return (
    <Box ref={wrapperRef} p={3} border="1px solid" borderColor="lightGrey" borderRadius="md">
      <Flex
        alignItems="center"
        justify="space-between"
        cursor="pointer"
        onClick={handleToggle}
      >
        <Flex direction="column" gap={1}>
          <Text fontWeight="500">{getLocaleDisplayName(language)}</Text>
          {!isOpen && completionText && (
            <Flex align="center" gap={1.5}>
              <CustomAlertTriangleIcon boxSize="14px" />
              <Text fontSize="12px" color="#989898">
                {completionText}
              </Text>
            </Flex>
          )}
        </Flex>
        {isOpen ? (
          <ChevronUpIcon boxSize="20px" />
        ) : compact ? (
          <Text fontSize="12px" cursor="pointer" pr={2}>
            Edit
          </Text>
        ) : (
          <ChevronDownIcon boxSize="20px" />
        )}
      </Flex>
      <Collapse in={isOpen}>
        <Flex direction="column" gap={4} mt={3}>
          <Flex direction="column" gap={5}>
            {Object.keys(headerFields).map((field, index) => (
              <LocalisedField
                key={field}
                section="header"
                field={field}
                fieldData={headerFields[field]}
                language={language}
                showLabel={!!headerFields[field]?.label}
                showDefaultValue={!!headerFields[field]?.defaultValue}
                sectionName={`Header Field ${index + 1}`}
                isVisible={
                  !!headerFields[field]?.label ||
                  !!headerFields[field]?.defaultValue
                }
                compact={compact}
                showWarnings={useNewDesign}
              />
            ))}
            {Object.keys(centralFields).map((field, index) => (
              <LocalisedField
                key={field}
                section="central"
                field={field}
                fieldData={centralFields[field]}
                language={language}
                showLabel={!!centralFields[field]?.label}
                showDefaultValue={!!centralFields[field]?.defaultValue}
                sectionName={`Central Field ${index + 1}`}
                isVisible={
                  !!centralFields[field]?.label ||
                  !!centralFields[field]?.defaultValue
                }
                compact={compact}
                showWarnings={useNewDesign}
              />
            ))}
            {Object.keys(detailsFields).map((field, index) => (
              <LocalisedField
                key={field}
                section="details"
                field={field}
                fieldData={detailsFields[field]}
                language={language}
                showLabel={!!detailsFields[field]?.label}
                showDefaultValue={!!detailsFields[field]?.defaultValue}
                sectionName={`Details Field ${index + 1}`}
                isVisible={
                  !!detailsFields[field]?.label ||
                  !!detailsFields[field]?.defaultValue
                }
                compact={compact}
                showWarnings={useNewDesign}
              />
            ))}
            {Object.keys(backFields).map((field, index) => (
              <LocalisedField
                key={field}
                section="back"
                field={field}
                fieldData={backFields[field]}
                language={language}
                showLabel={!!backFields[field]?.label}
                showDefaultValue={!!backFields[field]?.defaultValue}
                sectionName={`Back Field ${index + 1}`}
                isVisible={
                  !!backFields[field]?.label ||
                  !!backFields[field]?.defaultValue
                }
                compact={compact}
                showWarnings={useNewDesign}
              />
            ))}
          </Flex>
          <Flex mt={2} alignItems="center" justify="flex-end" gap={2}>
            <Link
              variant="orange"
              fontSize="12px"
              onClick={() => setIsRemoveModalOpen(true)}
            >
              Remove language
            </Link>
          </Flex>
        </Flex>
      </Collapse>
      <RemoveLanguageModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={handleRemoveLanguage}
        redesigned={useNewDesign}
      />
    </Box>
  );
};

export default LanguagesWrapper;
